import { useState, useEffect, useRef } from "react";
import { normalizeText } from "@/utils/textFormatter";
import { ResidueEntry } from "@/types/residue";

interface Option {
  value: string;
  label: string;
}
interface Errors {
  type: string;
  treatments: string;
  quantities: Record<string, string>;
}

interface UseResidueFormArgs {
  formEntry: ResidueEntry;
  residueOptions: Option[];
  editing: boolean;
  onFormEntryChange: (entry: ResidueEntry) => void;
  onSave: () => void;
}

export const useResidueForm = ({
  formEntry,
  residueOptions,
  editing,
  onFormEntryChange,
  onSave,
}: UseResidueFormArgs) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [userIsTyping, setUserIsTyping] = useState(false);
  const [search, setSearch] = useState(
    formEntry.residueType ? residueOptions.find((opt) => opt.value === formEntry.residueType)?.label ?? "" : ""
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [touched, setTouched] = useState({
    type: false,
    treatments: false,
    quantities: {} as Record<string, boolean>,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (!userIsTyping) {
      if (formEntry.residueType) {
        const matchingOption = residueOptions.find((opt) => opt.value === formEntry.residueType);
        if (matchingOption) {
          setSearch(matchingOption.label);
        }
      } else {
        setSearch("");
      }
    }
  }, [formEntry.residueType, residueOptions, userIsTyping]);

  useEffect(() => {
    if (!formEntry.residueType && formEntry.treatments.length === 0) {
      setTouched({ type: false, treatments: false, quantities: {} });
      setSubmitAttempted(false);
    }
  }, [formEntry.residueType, formEntry.treatments.length]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const dropdownElement = dropdownRef.current;
      
      if (!dropdownElement) return;
      
      // Check if click is within the dropdown bounds
      const rect = dropdownElement.getBoundingClientRect();
      const isWithinDropdownBounds = e.clientX >= rect.left && e.clientX <= rect.right && 
                                   e.clientY >= rect.top && e.clientY <= rect.bottom;
      
      if (isWithinDropdownBounds) {
        return;
      }
      
      // Only close if click is truly outside and not currently scrolling
      if (!dropdownElement.contains(e.target as Node) && !isScrolling) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isScrolling]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowDropdown(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const formDataForValidation = { ...formEntry, search };
  const errors = getErrors({ formData: formDataForValidation, touched, submitAttempted, residueOptions });

  const isValid =
    !errors.type &&
    !errors.treatments &&
    Object.keys(errors.quantities).length === 0 &&
    !!formEntry.residueType &&
    formEntry.treatments.length > 0 &&
    formEntry.treatments.every((t) => Number(t.quantity) > 0);

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserIsTyping(true);
    setSearch(e.target.value);
    setShowDropdown(true);
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown((prev) => !prev);
  };

  const handleTypeBlur = () => {
    setTimeout(() => {
      if (!document.activeElement || !dropdownRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        if (search && !formEntry.residueType) {
          updateTypeValueFromSearch();
        }
        setUserIsTyping(false);
      }
    }, 300);
  };

  const updateTypeValueFromSearch = () => {
    setTouched((prev) => ({ ...prev, type: true }));

    if (!search.trim()) {
      if (formEntry.residueType) {
        onFormEntryChange({ ...formEntry, residueType: "", treatments: [] });
      }
      return;
    }

    const match = residueOptions.find(
      (opt) =>
        normalizeText(opt.label).includes(normalizeText(search)) ||
        normalizeText(search).includes(normalizeText(opt.label))
    );

    if (match) {
      if (match.value !== formEntry.residueType) {
        onFormEntryChange({ ...formEntry, residueType: match.value, treatments: [] });
      }
    } else {
      onFormEntryChange({ ...formEntry, residueType: "", treatments: [] });
    }
  };

  const handleTypeSelect = (option: Option, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setUserIsTyping(false);
    setSearch(option.label);
    setShowDropdown(false);
    setTouched((prev) => ({ ...prev, type: true }));

    if (option.value !== formEntry.residueType) {
      onFormEntryChange({ ...formEntry, residueType: option.value, treatments: [] });
    }
  };

  const handleTreatmentToggle = (treatmentType: string, checked: boolean) => {
    let newTreatments = [...formEntry.treatments];

    if (checked) {
      newTreatments.push({ treatmentTypeId: treatmentType, quantity: "" });
    } else {
      newTreatments = newTreatments.filter((t) => t.treatmentTypeId !== treatmentType);
    }

    if (newTreatments.length > 0) {
      setTouched((prev) => ({ ...prev, treatments: true }));
    }

    onFormEntryChange({ ...formEntry, treatments: newTreatments });
  };

  const handleQuantityChange = (treatmentType: string, newQuantity: number | "") => {
    setTouched((prev) => ({
      ...prev,
      quantities: { ...prev.quantities, [treatmentType]: true },
    }));
    const newTreatments = formEntry.treatments.map((t) =>
      t.treatmentTypeId === treatmentType ? { ...t, quantity: newQuantity } : t
    );
    onFormEntryChange({ ...formEntry, treatments: newTreatments });
  };

  const handleQuantityBlur = (treatmentType: string) => {
    setTouched((prev) => ({
      ...prev,
      quantities: { ...prev.quantities, [treatmentType]: true },
    }));
  };

  const handleSaveClick = () => {
    setSubmitAttempted(true);
    if (isValid) onSave();
  };

  const filteredOptions = (
    search.trim() === ""
      ? residueOptions.filter((opt) => !editing || opt.value !== formEntry.residueType)
      : residueOptions.filter((opt) => 
          normalizeText(opt.label).includes(normalizeText(search)) && 
          (!editing || opt.value !== formEntry.residueType)
        )
  ).sort((a, b) => a.label.localeCompare(b.label));

  return {
    dropdownRef,
    search,
    showDropdown,
    errors,
    isValid,
    filteredOptions,
    isScrolling,
    setIsScrolling,
    handleTypeChange,
    handleInputClick,
    handleTypeBlur,
    handleTypeSelect,
    handleTreatmentToggle,
    handleQuantityChange,
    handleQuantityBlur,
    handleSaveClick,
  };
};

function getErrors({
  formData,
  touched,
  submitAttempted,
  residueOptions,
}: {
  formData: ResidueEntry & { search: string };
  touched: { type: boolean; treatments: boolean; quantities: Record<string, boolean> };
  submitAttempted: boolean;
  residueOptions: Option[];
}): Errors {
  return {
    type:
      (touched.type || submitAttempted) &&
      (!formData.residueType || !residueOptions.some((opt) => opt.value === formData.residueType))
        ? "Selecione um tipo de resíduo válido."
        : "",
    treatments:
      (touched.treatments || submitAttempted) && (!formData.treatments || formData.treatments.length === 0)
        ? "Selecione pelo menos um tipo de tratamento."
        : "",
    quantities: formData.treatments.reduce((acc, treatment) => {
      if (
        (touched.quantities[treatment.treatmentTypeId] || submitAttempted) &&
        (treatment.quantity === "" || Number(treatment.quantity) <= 0)
      ) {
        acc[treatment.treatmentTypeId] = "A quantidade deve ser um valor superior a 0";
      }
      return acc;
    }, {} as Record<string, string>),
  };
}
